import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	Subject,
	take,
	takeUntil
} from 'rxjs';

import { ScheduledJob, ScheduledJobRequest } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	CreateScheduledJobApiInput,
	CreateScheduledJobMutationResult,
	DeleteScheduledJobApiInput,
	QueryScheduledJobRequestsApiInput,
	ScheduledJobsApiService,
	UpdateScheduledJobApiInput,
	UpdateScheduledJobMutationResult
} from '../api/scheduled-jobs-api.service';

import {
	CancelScheduledJobMutationGQL,
	CreateScheduledJobMutationGQL,
	DeleteScheduledJobMutationGQL,
	ScheduledJobQueryGQL,
	ScheduledJobRequestsConnectionInput,
	ScheduledJobRequestsQueryGQL,
	ScheduledJobsQueryGQL,
	ScheduledJobType,
	StartScheduledJobMutationGQL,
	UpdateScheduledJobMutationGQL
} from './graphql/generated/cloud-sys-api';
import { mapScheduledJobFromSysApi } from './mappers/map-scheduled-job';
import { mapScheduledJobRequestFromSysApi } from './mappers/map-scheduled-job-request';

export const PAGE_SIZE = 3000;

interface QueryResult<TData> {
	isLoading: boolean;
	data: TData;
	error?: Error;
}

@Injectable({ providedIn: 'root' })
export class SysApiScheduledJobsApiService implements ScheduledJobsApiService {
	private destroy$ = new Subject<void>();
	private isInitialized = false;
	private readonly logger: ILogger;
	private readonly scheduledJobs = new BehaviorSubject<QueryResult<ScheduledJob[]>>({ isLoading: false, data: [] });
	private readonly jobNodeIdToRequestsQueryResult = new Map<
		string,
		BehaviorSubject<QueryResult<ScheduledJobRequest[]>>
	>();

	// the presumption is the feature flag service is initialized before this service is initialized.
	private readonly scheduledJobsFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-scheduled-jobs'),
		{
			initialValue: false
		}
	);

	private readonly scheduledJobsRebootFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-scheduled-jobs-reboot'),
		{
			initialValue: false
		}
	);

	constructor(
		logger: ILogger,
		oktaService: OktaInterfaceService,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService,
		private readonly scheduledJobQuery: ScheduledJobQueryGQL,
		private readonly scheduledJobsQuery: ScheduledJobsQueryGQL,
		private readonly scheduledJobRequestsQuery: ScheduledJobRequestsQueryGQL,
		private readonly createScheduledJobMutation: CreateScheduledJobMutationGQL,
		private readonly updateScheduledJobMutation: UpdateScheduledJobMutationGQL,
		private readonly cancelScheduledJobMutation: CancelScheduledJobMutationGQL,
		private readonly startScheduledJobMutation: StartScheduledJobMutationGQL,
		private readonly deleteScheduledJobMutation: DeleteScheduledJobMutationGQL
	) {
		this.logger = logger.createScopedLogger('SysApiScheduledJobsApiService');

		monitorLoginState(oktaService, {
			onLogIn: this.initService.bind(this),
			onLogOut: this.suspendService.bind(this)
		});
	}

	public getScheduledJobs$(): Observable<ScheduledJob[]> {
		return this.scheduledJobs.asObservable().pipe(map((result) => result.data));
	}

	public getScheduledJobsCount$(): Observable<number> {
		return this.getScheduledJobs$().pipe(map((jobs) => jobs.length));
	}

	public fetchScheduledJob$(jobId: string, options?: { skipCache?: boolean }): Observable<ScheduledJob | null> {
		return this.scheduledJobQuery
			.fetch({ variables: { jobId }, fetchPolicy: options?.skipCache ? 'network-only' : 'cache-first' })
			.pipe(
				takeUntil(this.destroy$),
				take(1),
				filter((result) => result.data !== undefined),
				map((result) => {
					if (!result.data?.scheduledJob) {
						throw new Error(`Scheduled job with ID ${jobId} not found`);
					}

					return mapScheduledJobFromSysApi(result.data.scheduledJob);
				})
			);
	}

	public isLoadingJobs$(): Observable<boolean> {
		return this.scheduledJobs.asObservable().pipe(
			map((result) => result.isLoading),
			distinctUntilChanged()
		);
	}

	public loadScheduledJobs(_opts?: { refresh: boolean }): void {
		let updatedAfter: string | undefined;

		// For now, I'm commenting out the code that uses the refresh flag. The reason is,
		// only querying for jobs that have been updated after a certain period of time doesn't work
		// when jobs are deleted. This also doesn't work when a job is deleted due to the TTL expiring.
		// So, Until we have support for subscriptions and being notified when jobs are deleted, the
		// cleanest solution to keeping the cache and UI in sync with the server is to always reload
		// all items.

		// if (opts?.refresh === true) {
		// 	// find the largest updatedAt time from all the current jobs
		// 	let latestUpdateTime = new Date('Jan 1, 1970');
		// 	for (const job of this.scheduledJobs.getValue().data) {
		// 		if (job.updatedAt > latestUpdateTime) {
		// 			latestUpdateTime = job.updatedAt;
		// 		}
		// 	}
		// 	// Add 1ms to that time to avoid refetching the last job.
		// 	updatedAfter = new Date(latestUpdateTime.getTime() + 1).toISOString();
		// }

		if (this.scheduledJobs.getValue().isLoading === true) {
			return;
		}

		// If updatedAfter is provided, we need to retain our current list of jobs.
		// Otherwise, we clear the list and do a full reload.
		if (updatedAfter) {
			this.scheduledJobs.next({
				isLoading: true,
				data: this.scheduledJobs.getValue().data,
				error: undefined
			});
		} else {
			this.scheduledJobs.next({ isLoading: true, data: [], error: undefined });
		}

		this.queryScheduledJobs(null, updatedAfter);
	}

	public getScheduledJobRequests$(input: QueryScheduledJobRequestsApiInput): Observable<ScheduledJobRequest[]> {
		const requestsQueryResult = this.getScheduledJobRequestsQueryResult(input);
		return requestsQueryResult.asObservable().pipe(map((result) => result.data));
	}

	public isLoadingScheduledJobRequests$(input: QueryScheduledJobRequestsApiInput): Observable<boolean> {
		const requestsQueryResult = this.getScheduledJobRequestsQueryResult(input);
		return requestsQueryResult.asObservable().pipe(
			map((result) => result.isLoading),
			distinctUntilChanged()
		);
	}

	public loadScheduledJobRequests(input: QueryScheduledJobRequestsApiInput): void {
		const requestsQueryResult = this.getScheduledJobRequestsQueryResult(input);
		requestsQueryResult.next({ isLoading: true, data: [], error: undefined });
		this.queryScheduledJobRequests(input, null, requestsQueryResult);
	}

	public createScheduledJob(input: CreateScheduledJobApiInput): Observable<CreateScheduledJobMutationResult> {
		return this.createScheduledJobMutation
			.mutate({
				variables: { input }
			})
			.pipe(
				filter((result) => result.data !== undefined),
				map((result) => {
					if (result.data?.createScheduledJob.scheduledJob) {
						return {
							scheduledJob: mapScheduledJobFromSysApi(result.data.createScheduledJob.scheduledJob)
						};
					}

					if (result.data?.createScheduledJob.error) {
						throw result.data.createScheduledJob.error;
					}

					throw new Error('CreateScheduledJob mutation returned no data');
				}),
				catchError((error) => {
					this.logger.error('createScheduledJob', 'Error', error);
					throw error;
				})
			);
	}

	public updateScheduledJob(input: UpdateScheduledJobApiInput): Observable<UpdateScheduledJobMutationResult> {
		return this.updateScheduledJobMutation
			.mutate({
				variables: { input }
			})
			.pipe(
				filter((result) => result.data !== undefined),
				map((result) => {
					if (result.data?.updateScheduledJob.scheduledJob) {
						return {
							scheduledJob: mapScheduledJobFromSysApi(result.data.updateScheduledJob.scheduledJob)
						};
					}

					if (result.data?.updateScheduledJob.error) {
						throw result.data.updateScheduledJob.error;
					}

					throw new Error('UpdateScheduledJob mutation returned no data');
				}),
				catchError((error) => {
					this.logger.error('updateScheduledJob', 'Error', error);
					throw error;
				})
			);
	}

	public cancelScheduledJob(jobNodeId: string): Observable<void> {
		return this.cancelScheduledJobMutation
			.mutate({
				variables: { input: { id: jobNodeId } }
			})
			.pipe(
				filter((result) => result.data !== undefined),
				map((result) => {
					if (result.data?.cancelScheduledJob.error) {
						throw result.data.cancelScheduledJob.error;
					}

					return;
				}),
				catchError((error) => {
					this.logger.error('cancelScheduledJob', 'Error', error);
					throw error;
				})
			);
	}

	public startScheduledJob(jobNodeId: string): Observable<void> {
		return this.startScheduledJobMutation
			.mutate({
				variables: { jobNodeId }
			})
			.pipe(
				filter((result) => result.data !== undefined),
				map((result) => {
					if (result.data?.startScheduledJob.error) {
						throw result.data.startScheduledJob.error;
					}

					return;
				}),
				catchError((error) => {
					this.logger.error('startScheduledJob', 'Error', error);
					throw error;
				})
			);
	}

	public deleteScheduledJob(input: DeleteScheduledJobApiInput): Observable<void> {
		return this.deleteScheduledJobMutation
			.mutate({
				variables: {
					input: { id: input.id }
				}
			})
			.pipe(
				map((result) => {
					if (
						result.data?.deleteScheduledJob &&
						'error' in result.data.deleteScheduledJob &&
						result.data.deleteScheduledJob.error
					) {
						throw result.data.deleteScheduledJob.error;
					}

					return;
				}),
				catchError((error) => {
					this.logger.error('deleteScheduledJob', 'Error', error);
					throw error;
				})
			);
	}

	private queryScheduledJobs(afterCursor: string | null, updatedAfter?: string): void {
		this.scheduledJobsQuery
			.fetch({
				variables: {
					paginationArgs: {
						first: PAGE_SIZE,
						after: afterCursor
					},
					updatedAfter: updatedAfter ?? null
				},
				fetchPolicy: 'network-only'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					if (!result.data) {
						return;
					}

					let jobs = result.data.scheduledJobsConnection.edges
						.filter((edge) => !!edge.node)
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						.map((edge) => mapScheduledJobFromSysApi(edge.node!));

					// Only show firmware update jobs when reboot jobs feature is disabled
					if (!this.scheduledJobsRebootFeatureEnabled()) {
						jobs = jobs.filter((job) => job.type === ScheduledJobType.FirmwareUpdate);
					}

					const updatedJobsList = [...this.scheduledJobs.getValue().data];
					for (const job of jobs) {
						// If the job already exists in our list, replace it
						const existingJobIndex = updatedJobsList.findIndex((j) => j.id === job.id);
						if (existingJobIndex >= 0) {
							updatedJobsList[existingJobIndex] = job;
						} else {
							updatedJobsList.push(job);
						}
					}

					const pageInfo = result.data.scheduledJobsConnection.pageInfo;
					if (pageInfo.hasNextPage && pageInfo.endCursor) {
						this.scheduledJobs.next({
							isLoading: true,
							data: updatedJobsList,
							error: undefined
						});
						this.queryScheduledJobs(pageInfo.endCursor, updatedAfter);
					} else {
						this.scheduledJobs.next({
							isLoading: false,
							data: updatedJobsList,
							error: undefined
						});
					}
				},
				error: (error) => {
					this.logger.error('queryScheduledJobs', 'Error querying scheduled jobs', error);
					this.scheduledJobs.next({ isLoading: false, data: [], error });
				}
			});
	}

	private queryScheduledJobRequests(
		input: ScheduledJobRequestsConnectionInput,
		afterCursor: string | null,
		result: BehaviorSubject<QueryResult<ScheduledJobRequest[]>>
	): void {
		this.scheduledJobRequestsQuery
			.fetch({
				variables: {
					input: input,
					paginationArgs: {
						first: PAGE_SIZE,
						after: afterCursor
					},
					requestScheduledRebootJobs: this.scheduledJobsRebootFeatureEnabled()
				},
				fetchPolicy: 'no-cache'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					if (!response.data) {
						return;
					}

					const requests = response.data.scheduledJobRequestsConnection.edges
						.map((edge) => edge.node)
						.filter((node) => node !== null)
						.map((node) => mapScheduledJobRequestFromSysApi(node))
						.filter((data) => data !== undefined);

					const pageInfo = response.data.scheduledJobRequestsConnection.pageInfo;
					if (pageInfo.hasNextPage && pageInfo.endCursor) {
						result.next({
							isLoading: true,
							data: [...result.getValue().data, ...requests],
							error: undefined
						});
						this.queryScheduledJobRequests(input, pageInfo.endCursor, result);
					} else {
						result.next({
							isLoading: false,
							data: [...result.getValue().data, ...requests],
							error: undefined
						});
					}
				},
				error: (error) => {
					this.logger.error('queryScheduledJobRequests', `Error querying requests for job: ${input}`, error);
					result.next({ isLoading: false, data: [], error });
				}
			});
	}

	private getScheduledJobRequestsQueryResult(
		input: ScheduledJobRequestsConnectionInput
	): BehaviorSubject<QueryResult<ScheduledJobRequest[]>> {
		const id = input.scheduledJobJobId ?? input.scheduledJobNodeId;
		let requestsQueryResult = this.jobNodeIdToRequestsQueryResult.get(id);
		if (!requestsQueryResult) {
			requestsQueryResult = new BehaviorSubject<QueryResult<ScheduledJobRequest[]>>({
				isLoading: false,
				data: [],
				error: undefined
			});
			this.jobNodeIdToRequestsQueryResult.set(id, requestsQueryResult);
		}

		return requestsQueryResult;
	}

	private initService(): void {
		if (!this.scheduledJobsFeatureEnabled()) {
			return;
		}
		if (this.isInitialized) {
			return;
		}

		this.isInitialized = true;
		this.destroy$ = new Subject();
		this.loadScheduledJobs();
	}

	private suspendService(): void {
		this.isInitialized = false;
		this.destroy$.next();
		this.destroy$.complete();
	}
}
